import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LayoutDashboard, BookOpen, MessageSquare, LogOut, Menu, Trophy, Award, BarChart3, UserCircle2, Timer, Users, Wallet, Flame, Settings as SettingsIcon, Rss, TrendingUp } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import NotificationsBell from "@/components/NotificationsBell";
import BrandLogo from "@/components/BrandLogo";
import SuspendedAccessDialog from "@/components/SuspendedAccessDialog";
import MobileStudentLayout from "./MobileStudentLayout";
import { ShortcutCheatsheet } from "@/lib/keyboardShortcuts";

function Sidebar({ onNav }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  // Live "cards due today" count for the Study nav pill (FSRS-driven).
  // Polled every 60s so the badge ticks down as the student works through reviews.
  const [dueToday, setDueToday] = useState(null);
  // Billing attention badge — amber dot when any enrollment is expiring soon,
  // auto-expired, or suspended. Fetched once when the sidebar mounts.
  const [billingAttention, setBillingAttention] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const fetchDue = () => {
      api.get("/student/smart-review/summary")
        .then((r) => { if (!cancelled) setDueToday(r.data?.due_today ?? 0); })
        .catch(() => {});
    };
    fetchDue();
    const t = setInterval(fetchDue, 60_000);
    api.get("/student/my-enrollments")
      .then((r) => {
        if (cancelled) return;
        const rows = (r.data && r.data.enrolled) || [];
        const flag = rows.some((c) => c.suspended || c.auto_expired || c.expiring_soon);
        setBillingAttention(flag);
      })
      .catch(() => {});
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  const items = [
    { to: "/student", icon: LayoutDashboard, label: "Dashboard", end: true },
    { to: "/student/courses", icon: BookOpen, label: "Courses" },
    { to: "/student/study", icon: Flame, label: "Study", badge: dueToday, gradient: true },
    { to: "/student/friends", icon: Users, label: "Friends" },
    // Social + gamification bits that live only in the mobile "More"
    // menu — add them here so tablet / desktop users can reach them too.
    { to: "/student/feed", icon: Rss, label: "Feed" },
    { to: "/student/progress-card", icon: TrendingUp, label: "Progress" },
    { to: "/student/analytics", icon: BarChart3, label: "Analytics" },
    { to: "/student/enrollments", icon: Wallet, label: "Billing & Access", dot: billingAttention },
    { to: "/student/leaderboard", icon: Trophy, label: "Leaderboard" },
    { to: "/student/badges", icon: Award, label: "Badges" },
    { to: "/student/profile", icon: UserCircle2, label: "Profile" },
    { to: "/student/settings", icon: SettingsIcon, label: "Settings" },
    { to: "/student/support", icon: MessageSquare, label: "Support" },
  ];
  return (
    <aside className="h-full w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col">
      <div className="h-16 px-4 flex items-center border-b border-slate-200">
        <BrandLogo size="md" />
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto min-h-0">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={onNav}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-50"}`
            }
            data-testid={`student-nav-${it.label.toLowerCase()}`}
          >
            <it.icon className="w-4 h-4" strokeWidth={1.5} />
            <span className="flex-1">{it.label}</span>
            {it.dot && (
              <span
                className="ml-auto h-2 w-2 rounded-full bg-amber-500 shadow-[0_0_0_2px_rgba(251,191,36,0.25)]"
                data-testid="sidebar-billing-attention-dot"
                title="Action needed on your billing"
                aria-label="Billing attention needed"
              />
            )}
            {it.badge != null && it.badge > 0 && (
              <span
                className="ml-auto inline-flex items-center justify-center min-w-[22px] h-[20px] px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold tabular-nums shadow-sm"
                data-testid="sidebar-due-today-badge"
                title={`${it.badge} card${it.badge === 1 ? "" : "s"} due today`}
              >
                {it.badge > 99 ? "99+" : it.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-200">
        <div className="flex items-center justify-between gap-2 px-3 mb-2">
          <div className="text-xs text-slate-500 truncate">{user.full_name}</div>
          <div className="flex items-center gap-1">
            <NotificationsBell testId="student-desktop-bell-btn" />
            <ThemeToggle />
          </div>
        </div>
        <Button variant="ghost" className="w-full justify-start" onClick={() => { logout(); nav("/login"); }} data-testid="student-logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
      </div>
    </aside>
  );
}

export default function StudentLayout() {
  const isMobile = useIsMobile(768);
  const [open, setOpen] = useState(false);

  // Native push registration: fires once when a student lands in the app
  // shell. Idempotent — safe to call on every remount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { ensureNativePushRegistered } = await import("@/lib/nativePush");
        if (!cancelled) await ensureNativePushRegistered();
      } catch { /* noop — web/no-native */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (isMobile) {
    return (
      <>
        <ShortcutCheatsheet />
        <MobileStudentLayout />
      </>
    );
  }

  return (
    // h-screen + overflow-hidden lock the outer flex to the viewport so the
    // sidebar stays put while the main pane scrolls internally. Previously
    // we used min-h-screen which let the whole document grow taller than
    // the viewport — on iPad + web the aside scrolled out of view alongside
    // the main content.
    <div className="h-screen flex bg-[#F8FAFC] dark:bg-slate-950 overflow-hidden">
      <ShortcutCheatsheet />
      <SuspendedAccessDialog />
      <div className="hidden lg:block h-full"><Sidebar /></div>
      <div className="flex-1 flex flex-col min-w-0 h-full">
        <header className="lg:hidden h-14 px-4 flex items-center justify-between border-b border-slate-200 bg-white shrink-0">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><Button variant="ghost" size="icon"><Menu /></Button></SheetTrigger>
            <SheetContent side="left" className="p-0 w-64"><Sidebar onNav={() => setOpen(false)} /></SheetContent>
          </Sheet>
          <div className="font-display font-bold flex items-center"><BrandLogo size="sm" /></div>
          <div />
        </header>
        <main className="flex-1 p-6 lg:p-10 overflow-y-auto"><Outlet /></main>
      </div>
    </div>
  );
}
